/**
 * Error Boundary Component
 * Catches and handles React errors
 */

import React, { Component, ReactNode } from 'react';
import styled from 'styled-components';

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: var(--spacing-xl);
  background: var(--bg-primary);
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 64px;
  margin-bottom: var(--spacing-lg);
`;

const ErrorTitle = styled.h2`
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
`;

const ErrorMessage = styled.p`
  color: var(--text-secondary);
  margin-bottom: var(--spacing-lg);
  max-width: 400px;
`;

const RetryButton = styled.button`
  padding: var(--spacing-md) var(--spacing-xl);
  background: var(--accent-green);
  color: var(--text-primary);
  border-radius: var(--radius-lg);
  font-weight: var(--font-semibold);
  border: none;
  cursor: pointer;
  transition: opacity var(--transition);

  &:hover {
    opacity: 0.9;
  }
`;

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorContainer>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorTitle>Oops! Something went wrong</ErrorTitle>
          <ErrorMessage>
            {this.state.error?.message || 'An unexpected error occurred. Please try again.'}
          </ErrorMessage>
          <RetryButton onClick={this.handleRetry}>
            Reload App
          </RetryButton>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
